/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";

const FlagIcon = ({ size = 10, color = "white" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.8" stroke={color} width={size} height={size}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v1.5M3 21v-6m0 0 2.77-.693a9 9 0 0 1 6.208.682l.108.054a9 9 0 0 0 6.086.71l3.114-.732a48.524 48.524 0 0 1-.005-10.499l-3.11.732a9 9 0 0 1-6.085-.711l-.108-.054a9 9 0 0 0-6.208-.682L3 4.5M3 15V4.5" />
  </svg>
);

const QuestionPalette = ({
  questions = [],
  currentIndex,
  selectedAnswers = {},
  markedForReview = {},
  questionType,
  handleQuestionSelect,
  visitedMap = {},
}) => {
  const reviewMap = markedForReview?.[questionType] || {};


  const getStatus = (question, index) => {
    const isAnswered = !!selectedAnswers?.[question.id];
    const isMarked = reviewMap?.[question.id] === true;
    const isCurrent = currentIndex === index;
    const isVisited = visitedMap[index] === true; 

    // if (isCurrent) return "current";
    if (isAnswered && isMarked) return "answeredMarked";
    if (isMarked) return "marked";
    if (isAnswered) return "answered";
    if (isCurrent || isVisited) return "notAnswered";
    return "notVisited";
  };

  const statusStyles = {
    notVisited: { bg: "#f0f0f0", border: "#c8c8c8", color: "#555" },
    notAnswered: { bg: "#fff0f0", border: "#f87171", color: "#ef4444" },
    answered: { bg: "#f0fff4", border: "#4ade80", color: "#16a34a" },
    marked: { bg: "#faf5ff", border: "#c084fc", color: "#9333ea" },
    answeredMarked: { bg: "#faf5ff", border: "#a855f7", color: "#7c3aed" },
    current: { bg: "#fff5f5", border: "#f87171", color: "#ef4444" },
  };

  const answeredCount = questions.filter((q) =>
    !!selectedAnswers?.[q.id] && !reviewMap?.[q.id]
  ).length;

  const markedCount = Object.values(reviewMap || {}).filter(Boolean).length;
  const total = questions.length;
  const notAnsweredCount = questions.filter((q, i) =>
    (visitedMap[i] === true || i === currentIndex) && !selectedAnswers?.[q.id] && !reviewMap?.[q.id]
  ).length;
  const notVisitedCount = questions.filter((_, i) =>
    !visitedMap[i] && i !== currentIndex
  ).length;

  const LegendItem = ({ label, borderColor }) => (
    <div className="flex items-center gap-2">
      <div
        className="w-5 h-5 rounded-full flex-shrink-0 border-2"
        style={{ borderColor }}
      />
      <span className="text-xs text-gray-700">{label}</span>
    </div>
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        padding: "16px 16px",
        boxSizing: "border-box",
      }}
    >
      {/* Title */}
      <p className="font-bold text-sm tracking-wide mb-4">QUESTION PALETTE</p>

      {/* Legend */}
      <div className="grid grid-cols-2 gap-x-2 gap-y-2 mb-3">
        <LegendItem label="Not Visited" borderColor="#9ca3af" />
        <LegendItem label="Not Answered" borderColor="#f87171" />
        <LegendItem label="Answered" borderColor="#4ade80" />
        <LegendItem label="Marked" borderColor="#c084fc" />

        {/* Answered & Marked */}
        <div className="col-span-2 flex items-center gap-2">
          <div className="relative w-5 h-5 flex-shrink-0">
            <div className="w-5 h-5 rounded-full border-2" style={{ borderColor: "#a855f7", backgroundColor: "#faf5ff" }} />
            <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full flex items-center justify-center" style={{ backgroundColor: "#7c3aed" }}>
              <FlagIcon size={7} color="white" />
            </div>
          </div>
          <span className="text-xs text-gray-700">Answered &amp; Marked</span>
        </div>
      </div>

      <hr className="border-gray-200 mb-3" />

      {/* Question Grid */}
      <div
        className="overflow-y-auto px-1 pt-2"
        style={{
          flex: 1,
          minHeight: 0,
          scrollbarWidth: "thin",
          scrollbarColor: "#c7c7c7 #f1f1f1",
        }}
      >
        <div className="grid grid-cols-6 gap-1.5" style={{ overflow: "visible" }}>
          {questions?.map((question, index) => {
            const status = getStatus(question, index);
            const style = statusStyles[status];
            const isCurrent = currentIndex === index;
            const isAnsweredMarked = reviewMap?.[question.id] === true && !!selectedAnswers?.[question.id];

            return (
              <div key={`${question.id}-${index}`} 
                role="button"
                tabIndex={0}
                onClick={() => handleQuestionSelect(index)}
                onKeyDown={(e) => e.key === 'Enter' && handleQuestionSelect(index)}
                className="relative flex items-center justify-center rounded-lg cursor-pointer font-semibold text-xs transition-transform hover:scale-105"
                style={{
                  aspectRatio: "1",
                  backgroundColor: style.bg,
                  border: `1.5px solid ${isCurrent ? "#f87171" : style.border}`,
                  color: style.color,
                  boxShadow: isCurrent ? "0 0 0 2px #fecaca" : "none",
                }}
              >
                {index + 1}
                {isAnsweredMarked && (
                  <div className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: "#7c3aed" }}>
                    <FlagIcon size={9} color="white" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <hr className="border-gray-200 mt-4 mb-3" />

      {/* Stat Badges */}
      <div className="grid grid-cols-2 gap-2">
        <StatBadge label="Answered" count={answeredCount} textColor="#16a34a" bgColor="#f0fff4" />
        <StatBadge label="Not Answered" count={notAnsweredCount} textColor="#ef4444" bgColor="#fff0f0" />
        <StatBadge label="Marked for Review" count={markedCount} textColor="#9333ea" bgColor="#faf5ff" />
        <StatBadge label="Not Visited" count={notVisitedCount} textColor="#4b5563" bgColor="#f0f0f0" />
      </div>
    </div>
  );
};

const StatBadge = ({ label, count, textColor, bgColor }) => (
  <div className="bg-white border border-gray-200 rounded-xl px-3 py-2 flex items-center gap-2 w-full min-w-0 min-h-[52px]">
    <div
      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: bgColor }}
    >
      <span className="text-xs font-bold" style={{ color: textColor }}>
        {count}
      </span>
    </div>
    <span className="text-xs text-gray-500 font-normal leading-tight break-words">
      {label}
    </span>
  </div>
);

export default QuestionPalette;